import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  computeSignal,
  pendingSpeech,
  readGenerationSummary,
  signalTickMs,
  useGenerationWatch,
  type SignalInput,
  type SignalView,
  type SpeechItem,
  type SummaryOf,
} from '@entities/Generation';
import { trackEvent } from '@shared/analytics';

const sameSignal = (a: SignalView | null, b: SignalView | null) => JSON.stringify(a) === JSON.stringify(b);
/** Неразрывный пробел: невидимый хвост, чтобы live-область повторила тот же текст. */
const NBSP = String.fromCharCode(0xa0);

/** Текст скрытой live-области: «Генерируем travel», «Готово: travel, 2 карточки», «Не получилось: travel». */
export function speechText(t: TFunction, items: readonly SpeechItem[]): string {
  const parts: string[] = [];
  for (const item of items) {
    const summary = item.summary;
    if (!summary) continue;
    const title = summary.kind === 'topic' ? t('generations.topic', { topic: summary.title }) : summary.title;
    if (item.phase === 'started') parts.push(t('homeComposer.signal.live.started', { title }));
    else if (summary.status === 'ready')
      parts.push(t('homeComposer.signal.live.ready', { title, cards: t('generations.cards', { count: summary.counts.total }) }));
    else parts.push(t('homeComposer.signal.live.failed', { title }));
  }
  return parts.join('. ');
}

export interface ComposerSignalOptions {
  /** Сигналу нет места: над строкой облачко, идёт подсветка, строка заблокирована. */
  blocked: boolean;
  surface: 'desktop' | 'phone';
  /** Убрали ✕ — куда вернуть фокус (компьютер — в поле слова). */
  onDismissed?: () => void;
}

/**
 * Сигнал у строки создания (ТЗ главной § 8): короткий живой статус нажатий этого захода со ссылкой к
 * записи «Недавно добавленных» — только пока её строки нет на экране. Решения — в чистой модели
 * слежения (`computeSignal`, `stepSignal`); здесь — шаг модели на каждое изменение слежения, кэша списка
 * и видимости вкладки и по таймеру, пока есть нажатия этого захода (`signalTickMs`: 100 мс, пока что-то
 * решается по времени, иначе 1 с), а ещё объявления скрытой live-области: начало и итог — по одному
 * разу, виден сигнал или нет.
 */
export function useComposerSignal({ blocked, surface, onDismissed }: ComposerSignalOptions) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [view, setView] = useState<SignalView | null>(null);
  const [live, setLive] = useState('');
  const viewRef = useRef<SignalView | null>(null);
  const held = useRef(false);
  const blockedRef = useRef(blocked);
  blockedRef.current = blocked;
  const tRef = useRef(t);
  tRef.current = t;
  const schedule = useRef<() => void>(() => {});

  const summaryOf = useCallback<SummaryOf>((id) => readGenerationSummary(qc, id), [qc]);

  useEffect(() => {
    let alive = true;
    let timer: number | undefined;
    let queued = false;
    // Свой шаг тоже меняет стор — его уведомление не должно запускать шаг заново.
    let stepping = false;
    let said = 0;

    const run = () => {
      queued = false;
      window.clearTimeout(timer);
      timer = undefined;
      if (!alive) return;
      const input: SignalInput = {
        tabVisible: typeof document === 'undefined' || document.visibilityState !== 'hidden',
        held: held.current,
        blocked: blockedRef.current,
      };
      const store = useGenerationWatch.getState();
      stepping = true;
      store.stepSignal(summaryOf, input);
      const speech = pendingSpeech(useGenerationWatch.getState(), summaryOf);
      if (speech.length) store.markSpoken(speech);
      stepping = false;
      const state = useGenerationWatch.getState();
      const next = computeSignal(state, summaryOf, input, Date.now());
      // Сигнал ушёл — курсор и фокус больше не на нём: элемент удаляется из DOM без pointerleave и blur, и
      // флаг иначе остановил бы 6-секундный отсчёт у следующего «Готово» этого захода.
      if (next === null) held.current = false;
      if (!sameSignal(viewRef.current, next)) {
        viewRef.current = next;
        setView(next);
      }
      const text = speechText(tRef.current, speech);
      if (text) {
        // Тот же текст подряд live-область не повторила бы — чередуем невидимый хвост.
        said += 1;
        setLive(said % 2 ? text : text + NBSP);
      }
      // Следующий шаг: часто, пока что-то решается по времени (300 мс, 150 мс, 1 с, 6 с), редко — когда
      // сигнал просто стоит; работы нет — шагов нет до следующего изменения.
      const delay = signalTickMs(state, summaryOf, Date.now());
      if (delay !== null) timer = window.setTimeout(run, delay);
    };
    const request = () => {
      if (stepping || queued || !alive) return;
      queued = true;
      queueMicrotask(run);
    };
    schedule.current = request;
    const offWatch = useGenerationWatch.subscribe(request);
    const offCache = qc.getQueryCache().subscribe(request);
    document.addEventListener('visibilitychange', request);
    run();
    return () => {
      alive = false;
      window.clearTimeout(timer);
      offWatch();
      offCache();
      document.removeEventListener('visibilitychange', request);
      schedule.current = () => {};
    };
  }, [qc, summaryOf]);

  // Облачко, подсветка или блокировка строки ушли — сигнал возвращается без ожидания шага.
  useEffect(() => {
    schedule.current();
  }, [blocked]);

  /** Нажали сигнал: запись раскрывается и прокручивается к ней; законченная генерация принята. */
  const show = useCallback(() => {
    const current = viewRef.current;
    if (!current || current.suppressed) return;
    trackEvent('home_generation_signal_clicked', {
      surface,
      status: current.state === 'failed' ? 'failed' : current.state === 'ready' || current.state === 'added' ? 'ready' : 'generating',
      count: current.count,
    });
    useGenerationWatch.getState().pressSignal(current.id, summaryOf(current.id));
  }, [surface, summaryOf]);

  const dismissed = useRef(onDismissed);
  dismissed.current = onDismissed;
  /** ✕ «Убрать» у «Не получилось»: итог принят, сигнал уходит. */
  const dismiss = useCallback(() => {
    const current = viewRef.current;
    if (!current) return;
    // Кнопка ✕ с фокусом исчезает вместе с «Не получилось» — blur не придёт; следующая запись сигнала
    // (если она есть) считает свои 6 с заново.
    held.current = false;
    useGenerationWatch.getState().dismiss(current.id);
    dismissed.current?.();
  }, []);

  /** Курсор или фокус на сигнале: отсчёт 6 с у «Готово» стоит. */
  const hold = useCallback((value: boolean) => {
    if (held.current === value) return;
    held.current = value;
    schedule.current();
  }, []);

  return { view, live, show, dismiss, hold };
}
